import { prisma } from './client';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export interface NaicsCodeResult {
  id: string;
  year: number;
  code: string;
  title: string;
  description: string | null;
  keywords?: string[] | null;
  crossRefs?: string[] | null;
  path: string;
  depth: number;
  // optional search scores
  fts_score?: number;
  trigram_score?: number;
  score?: number;
  // optional insight fields
  highlight_title?: string | null;
  highlight_description?: string | null;
  highlight_code?: string | null;
  title_similarity?: number;
  description_similarity?: number;
  code_similarity?: number;
}

export interface SearchNaicsOptions {
  year?: number; // default 2022
  limit?: number; // default 10
  offset?: number; // default 0
  depth?: number | number[]; // optional depth filter (single or multiple)
  includeChildren?: boolean; // default false
}

export interface KeywordSearchResult {
  keyword: string;
  code: string;
  title: string;
  year: number;
  depth: number;
  path: string;
}

export interface SearchKeywordsOptions {
  year?: number; // default 2022
  limit?: number; // default 25
  offset?: number; // default 0
  depth?: number | number[]; // optional depth filter
  excludeExcept?: boolean; // default true – filter out "except <term>" matches
}

/* -------------------------------------------------------------------------- */
/*                                 Utilities                                  */
/* -------------------------------------------------------------------------- */

function normalizeDepths(depth?: number | number[]): number[] | undefined {
  if (depth === undefined) return undefined;
  const arr = Array.isArray(depth) ? depth : [depth];
  const cleaned = Array.from(
    new Set(arr.map((d) => Number(d)).filter((d) => Number.isFinite(d)))
  );
  return cleaned.length > 0 ? cleaned : undefined;
}

function depthCondition(depth?: number | number[], column = 'depth'): string {
  const depths = normalizeDepths(depth);
  if (!depths) return '';
  if (depths.length === 1) return `AND ${column} = ${depths[0]}`;
  return `AND ${column} IN (${depths.join(',')})`;
}

/* -------------------------------------------------------------------------- */
/*                               Hierarchy Utils                              */
/* -------------------------------------------------------------------------- */

export async function getParent(
  code: string,
  year = 2022
): Promise<NaicsCodeResult | null> {
  const [record] = await prisma.$queryRaw<NaicsCodeResult[]>`
    SELECT path, depth
    FROM "NaicsCode"
    WHERE code = ${code} AND year = ${year}
    LIMIT 1;
  `;
  if (!record) return null;

  const parts = record.path.split('/');
  if (parts.length === 1) return null;

  const parentPath = parts.slice(0, -1).join('/');
  const [parent] = await prisma.$queryRaw<NaicsCodeResult[]>`
    SELECT id, year, code, title, description, keywords, "crossRefs", path, depth
    FROM "NaicsCode"
    WHERE path = ${parentPath} AND year = ${year}
    LIMIT 1;
  `;
  return parent ?? null;
}

export async function getSiblings(
  code: string,
  year = 2022
): Promise<NaicsCodeResult[]> {
  const [record] = await prisma.$queryRaw<{ path: string; depth: number }[]>`
    SELECT path, depth
    FROM "NaicsCode"
    WHERE code = ${code} AND year = ${year}
    LIMIT 1;
  `;
  if (!record) return [];

  const parts = record.path.split('/');
  if (parts.length === 1) return [];

  const parentPath = parts.slice(0, -1).join('/');
  return prisma.$queryRaw<NaicsCodeResult[]>`
    SELECT id, year, code, title, description, keywords, "crossRefs", path, depth
    FROM "NaicsCode"
    WHERE year = ${year}
      AND depth = ${record.depth}
      AND path LIKE ${parentPath + '/%'}
      AND code <> ${code};
  `;
}

export async function getChildren(
  code: string,
  year = 2022,
  opts?: { depth?: number | number[]; limit?: number; offset?: number }
): Promise<NaicsCodeResult[]> {
  const [record] = await prisma.$queryRaw<{ path: string; depth: number }[]>`
    SELECT path, depth
    FROM "NaicsCode"
    WHERE code = ${code} AND year = ${year}
    LIMIT 1;
  `;
  if (!record) return [];

  const depths = normalizeDepths(opts?.depth);
  const limitClause = opts?.limit ? `LIMIT ${Number(opts.limit)}` : '';
  const offsetClause = opts?.offset ? `OFFSET ${Number(opts.offset)}` : '';

  if (!depths) {
    // Immediate children only
    return prisma.$queryRawUnsafe<NaicsCodeResult[]>(
      `
      SELECT id, year, code, title, description, keywords, "crossRefs", path, depth
      FROM "NaicsCode"
      WHERE year = $1
        AND path LIKE $2
        AND depth = ${record.depth + 1}
      ORDER BY code
      ${limitClause} ${offsetClause};
    `,
      year,
      record.path + '/%'
    );
  }

  const depthFilter =
    depths.length === 1
      ? `AND depth = ${depths[0]}`
      : `AND depth IN (${depths.join(',')})`;

  return prisma.$queryRawUnsafe<NaicsCodeResult[]>(
    `
    SELECT id, year, code, title, description, keywords, "crossRefs", path, depth
    FROM "NaicsCode"
    WHERE year = $1
      AND path LIKE $2
      AND depth > ${record.depth}
      ${depthFilter}
    ORDER BY depth, code
    ${limitClause} ${offsetClause};
  `,
    year,
    record.path + '/%'
  );
}

export async function getDescendants(
  code: string,
  year = 2022
): Promise<NaicsCodeResult[]> {
  const [record] = await prisma.$queryRaw<{ path: string; depth: number }[]>`
    SELECT path, depth
    FROM "NaicsCode"
    WHERE code = ${code} AND year = ${year}
    LIMIT 1;
  `;
  if (!record) return [];

  return prisma.$queryRaw<NaicsCodeResult[]>`
    SELECT id, year, code, title, description, keywords, "crossRefs", path, depth
    FROM "NaicsCode"
    WHERE year = ${year}
      AND path LIKE ${record.path + '/%'}
      AND depth > ${record.depth};
  `;
}

export async function getAncestors(
  code: string,
  year = 2022
): Promise<NaicsCodeResult[]> {
  const [record] = await prisma.$queryRaw<{ path: string }[]>`
    SELECT path
    FROM "NaicsCode"
    WHERE code = ${code} AND year = ${year}
    LIMIT 1;
  `;
  if (!record) return [];

  const parts = record.path.split('/');
  const ancestorPaths = parts
    .slice(0, -1)
    .map((_, i) => parts.slice(0, i + 1).join('/'));

  return prisma.$queryRaw<NaicsCodeResult[]>`
    SELECT id, year, code, title, description, keywords, "crossRefs", path, depth
    FROM "NaicsCode"
    WHERE year = ${year}
      AND path = ANY(${ancestorPaths}::text[]);
  `;
}

/* -------------------------------------------------------------------------- */
/*                            Multi-Code Queries                              */
/* -------------------------------------------------------------------------- */

/**
 * List all NAICS codes at a given depth (or depths).
 * Useful for listing all 2-digit sector codes (depth=1), all 3-digit codes, etc.
 */
export async function getCodesByDepth(
  year = 2022,
  opts?: { depth?: number | number[]; limit?: number; offset?: number }
): Promise<{ results: NaicsCodeResult[]; total: number }> {
  const depths = normalizeDepths(opts?.depth);
  const depthFilter = depths
    ? depths.length === 1
      ? `AND depth = ${depths[0]}`
      : `AND depth IN (${depths.join(',')})`
    : '';
  const limitVal = opts?.limit ?? 1500;
  const offsetVal = opts?.offset ?? 0;

  const [rows, countRows] = await Promise.all([
    prisma.$queryRawUnsafe<NaicsCodeResult[]>(
      `
      SELECT id, year, code, title, description, keywords, "crossRefs", path, depth
      FROM "NaicsCode"
      WHERE year = $1
        ${depthFilter}
      ORDER BY code
      LIMIT ${Number(limitVal)} OFFSET ${Number(offsetVal)};
    `,
      year
    ),
    prisma.$queryRawUnsafe<{ total: number }[]>(
      `
      SELECT COUNT(*)::int AS total
      FROM "NaicsCode"
      WHERE year = $1
        ${depthFilter};
    `,
      year
    )
  ]);

  return { results: rows, total: countRows?.[0]?.total ?? rows.length };
}

/**
 * Get children for multiple parent codes in a single query.
 * Each parent's children are fetched via path LIKE patterns.
 */
export async function getChildrenMultiple(
  codes: string[],
  year = 2022,
  opts?: { depth?: number | number[]; limit?: number; offset?: number }
): Promise<NaicsCodeResult[]> {
  if (codes.length === 0) return [];

  // Look up all parent records at once
  const parents = await prisma.$queryRawUnsafe<
    { code: string; path: string; depth: number }[]
  >(
    `
    SELECT code, path, depth
    FROM "NaicsCode"
    WHERE year = $1
      AND code = ANY($2::text[])
    ORDER BY code;
  `,
    year,
    codes
  );

  if (parents.length === 0) return [];

  // Build OR conditions for each parent's children path
  const conditions = parents
    .map((p, i) => `(path LIKE $${i + 2} AND depth = ${p.depth + 1})`)
    .join(' OR ');
  const pathParams = parents.map((p) => p.path + '/%');

  const depths = normalizeDepths(opts?.depth);
  // When explicit depths are provided, use those instead of immediate-children constraint
  const depthOverride = depths
    ? depths.length === 1
      ? `AND depth = ${depths[0]}`
      : `AND depth IN (${depths.join(',')})`
    : '';

  const limitClause =
    opts?.limit != null ? `LIMIT ${Number(opts.limit)}` : '';
  const offsetClause =
    opts?.offset != null ? `OFFSET ${Number(opts.offset)}` : '';

  let query: string;
  const params: unknown[] = [year, ...pathParams];

  if (depthOverride) {
    // When depth is explicitly specified, match any descendant under the parents at those depths
    const pathConditions = parents
      .map((_, i) => `path LIKE $${i + 2}`)
      .join(' OR ');
    query = `
      SELECT id, year, code, title, description, keywords, "crossRefs", path, depth
      FROM "NaicsCode"
      WHERE year = $1
        AND (${pathConditions})
        ${depthOverride}
      ORDER BY code
      ${limitClause} ${offsetClause};
    `;
  } else {
    // Default: immediate children only
    query = `
      SELECT id, year, code, title, description, keywords, "crossRefs", path, depth
      FROM "NaicsCode"
      WHERE year = $1
        AND (${conditions})
      ORDER BY code
      ${limitClause} ${offsetClause};
    `;
  }

  return prisma.$queryRawUnsafe<NaicsCodeResult[]>(query, ...params);
}

/* -------------------------------------------------------------------------- */
/*                                Search Utils                                */
/* -------------------------------------------------------------------------- */

/**
 * Full‑text search over NAICS content using Postgres FTS.
 *
 * Query targets a tsvector column named `search` (built from code/title/description in the DB layer)
 * and ranks matches with `ts_rank` using `websearch_to_tsquery('english', query)`.
 *
 * Fields considered (via `search` tsvector):
 *  - code (text)
 *  - title
 *  - description
 *
 * Options:
 *  - year: restricts the NAICS vintage (default 2022)
 *  - depth: restricts results to an exact hierarchy depth when provided
 *  - limit/offset: pagination controls
 *  - includeChildren: when true, recursively includes descendants of top matches (descendants are scored 0)
 */
export async function searchNaicsFTS(
  query: string,
  options: SearchNaicsOptions = {
    year: 2022,
    limit: 10,
    offset: 0,
    includeChildren: false
  }
): Promise<{ results: NaicsCodeResult[]; total: number }> {
  const { year, limit, offset, depth, includeChildren } = options;

  const codeOnly = /^[0-9]+$/.test(query.trim());

  const ftsScoreExpr = codeOnly
    ? `ts_rank(to_tsvector('english', code), websearch_to_tsquery('english', $1))::double precision AS fts_score`
    : `(
                 ts_rank(to_tsvector('english', code), websearch_to_tsquery('english', $1)) * 1.0 +
                 ts_rank(to_tsvector('english', title), websearch_to_tsquery('english', $1)) * 0.8 +
                 ts_rank(to_tsvector('english', coalesce(description, '')), websearch_to_tsquery('english', $1)) * 0.6 +
                 ts_rank(to_tsvector('english', coalesce(array_to_string(keywords, ' '), '')), websearch_to_tsquery('english', $1)) * 0.7
               )::double precision AS fts_score`;

  const ftsWhere = codeOnly
    ? `to_tsvector('english', code) @@ websearch_to_tsquery('english', $1)`
    : `(
            to_tsvector('english', code) @@ websearch_to_tsquery('english', $1)
            OR to_tsvector('english', title) @@ websearch_to_tsquery('english', $1)
            OR to_tsvector('english', coalesce(description, '')) @@ websearch_to_tsquery('english', $1)
            OR to_tsvector('english', coalesce(array_to_string(keywords, ' '), '')) @@ websearch_to_tsquery('english', $1)
          )`;

  const depthClause = depthCondition(depth);
  const depthChildrenClause = depthCondition(depth, 'c.depth');

  if (!includeChildren) {
    const [rows, countRows] = await Promise.all([
      prisma.$queryRawUnsafe<NaicsCodeResult[]>(
        `
        SELECT id, code, title, description, keywords, "crossRefs", year, path, depth,
               ${ftsScoreExpr},
               ts_headline('english', title, websearch_to_tsquery('english', $1), 'StartSel=<mark>,StopSel=</mark>,MaxFragments=1,ShortWord=2') AS highlight_title,
               ts_headline('english', coalesce(description, ''), websearch_to_tsquery('english', $1), 'StartSel=<mark>,StopSel=</mark>,MaxFragments=2,ShortWord=2') AS highlight_description,
               ts_headline('english', code, websearch_to_tsquery('english', $1), 'StartSel=<mark>,StopSel=</mark>') AS highlight_code
        FROM "NaicsCode"
        WHERE year = $2
          ${depthClause}
          AND ${ftsWhere}
        ORDER BY fts_score DESC
        LIMIT $3 OFFSET $4;
      `,
        query,
        year,
        limit,
        offset
      ),
      prisma.$queryRawUnsafe<{ total: number }[]>(
        `
        SELECT COUNT(*)::int AS total
        FROM "NaicsCode"
        WHERE year = $2
          ${depthClause}
          AND ${ftsWhere};
      `,
        query,
        year
      )
    ]);
    return { results: rows, total: countRows?.[0]?.total ?? rows.length };
  }

  const rows = await prisma.$queryRawUnsafe<NaicsCodeResult[]>(
    `
    WITH RECURSIVE base AS (
      SELECT id, code, title, description, keywords, "crossRefs", year, path, depth,
             ${ftsScoreExpr},
             ts_headline('english', title, websearch_to_tsquery('english', $1), 'StartSel=<mark>,StopSel=</mark>,MaxFragments=1,ShortWord=2') AS highlight_title,
             ts_headline('english', coalesce(description, ''), websearch_to_tsquery('english', $1), 'StartSel=<mark>,StopSel=</mark>,MaxFragments=2,ShortWord=2') AS highlight_description,
             ts_headline('english', code, websearch_to_tsquery('english', $1), 'StartSel=<mark>,StopSel=</mark>') AS highlight_code
      FROM "NaicsCode"
      WHERE year = $2
        ${depthClause}
        AND ${ftsWhere}
      ORDER BY fts_score DESC
      LIMIT $3 OFFSET $4
    ),
    descendants AS (
      SELECT * FROM base
      UNION ALL
      SELECT c.id, c.code, c.title, c.description, c.year, c.path, c.depth,
             0::double precision AS fts_score,
             NULL::text AS highlight_title,
             NULL::text AS highlight_description,
             NULL::text AS highlight_code
      FROM "NaicsCode" c
      JOIN descendants d
        ON c.year = d.year
       AND c.path LIKE d.path || '/%'
       AND c.depth > d.depth
       ${depthChildrenClause}
    )
    SELECT id, code, title, description, year, path, depth, fts_score, highlight_title, highlight_description, highlight_code FROM descendants;
  `,
    query,
    year,
    limit,
    offset
  );
  const countRows = await prisma.$queryRawUnsafe<{ total: number }[]>(
    `
    SELECT COUNT(*)::int AS total
    FROM "NaicsCode"
    WHERE year = $2
      ${depthClause}
      AND ${ftsWhere};
  `,
    query,
    year
  );
  return { results: rows, total: countRows?.[0]?.total ?? rows.length };
}

/**
 * Trigram similarity search over NAICS fields using pg_trgm.
 *
 * Fields scored with `similarity()` and matched with the `%` operator:
 *  - title
 *  - description (coalesced to empty string)
 *  - code
 * The result row's `trigram_score` is the GREATEST of the three field similarities.
 *
 * Options behave the same as in FTS (year, depth, pagination, includeChildren).
 * When `includeChildren` is true, descendants of the top matches are included with score 0.
 */
export async function searchNaicsTrigram(
  query: string,
  options: SearchNaicsOptions = {
    year: 2022,
    limit: 10,
    offset: 0,
    includeChildren: false
  }
): Promise<{ results: NaicsCodeResult[]; total: number }> {
  const { year, limit, offset, depth, includeChildren } = options;

  const depthClause = depthCondition(depth);
  const depthChildrenClause = depthCondition(depth, 'c.depth');

  const codeOnly = /^[0-9]+$/.test(query.trim());
  const trigramScoreExpr = codeOnly
    ? `similarity(code, $1)::double precision AS trigram_score`
    : `(
               similarity(code, $1) * 1.0 +
               similarity(title, $1) * 0.8 +
               similarity(coalesce(description, ''), $1) * 0.6 +
               similarity(coalesce(array_to_string(keywords, ' '), ''), $1) * 0.6
             )::double precision AS trigram_score`;
  const trigramWhere = codeOnly
    ? `code % $1`
    : `(
            title % $1
            OR description % $1
            OR code % $1
            OR array_to_string(keywords, ' ') % $1
          )`;

  if (!includeChildren) {
    const [rows, countRows] = await Promise.all([
      prisma.$queryRawUnsafe<NaicsCodeResult[]>(
        `
        SELECT id, code, title, description, keywords, "crossRefs", year, path, depth,
               similarity(title, $1)::double precision AS title_similarity,
               similarity(coalesce(description, ''), $1)::double precision AS description_similarity,
               similarity(code, $1)::double precision AS code_similarity,
               ${trigramScoreExpr},
               ts_headline('english', title, websearch_to_tsquery('english', $1), 'StartSel=<mark>,StopSel=</mark>,MaxFragments=1,ShortWord=2') AS highlight_title,
               ts_headline('english', coalesce(description, ''), websearch_to_tsquery('english', $1), 'StartSel=<mark>,StopSel=</mark>,MaxFragments=2,ShortWord=2') AS highlight_description,
               ts_headline('english', code, websearch_to_tsquery('english', $1), 'StartSel=<mark>,StopSel=</mark>') AS highlight_code
        FROM "NaicsCode"
        WHERE year = $2
          ${depthClause}
          AND ${trigramWhere}
        ORDER BY trigram_score DESC
        LIMIT $3 OFFSET $4;
      `,
        query,
        year,
        limit,
        offset
      ),
      prisma.$queryRawUnsafe<{ total: number }[]>(
        `
        SELECT COUNT(*)::int AS total
        FROM "NaicsCode"
        WHERE year = $2
          ${depthClause}
          AND ${trigramWhere};
      `,
        query,
        year
      )
    ]);
    return { results: rows, total: countRows?.[0]?.total ?? rows.length };
  }

  const rows = await prisma.$queryRawUnsafe<NaicsCodeResult[]>(
    `
    WITH RECURSIVE base AS (
      SELECT id, code, title, description, keywords, "crossRefs", year, path, depth,
             similarity(title, $1)::double precision AS title_similarity,
             similarity(coalesce(description, ''), $1)::double precision AS description_similarity,
             similarity(code, $1)::double precision AS code_similarity,
             ${trigramScoreExpr},
             ts_headline('english', title, websearch_to_tsquery('english', $1), 'StartSel=<mark>,StopSel=</mark>,MaxFragments=1,ShortWord=2') AS highlight_title,
             ts_headline('english', coalesce(description, ''), websearch_to_tsquery('english', $1), 'StartSel=<mark>,StopSel=</mark>,MaxFragments=2,ShortWord=2') AS highlight_description,
             ts_headline('english', code, websearch_to_tsquery('english', $1), 'StartSel=<mark>,StopSel=</mark>') AS highlight_code
      FROM "NaicsCode"
      WHERE year = $2
        ${depthClause}
        AND ${trigramWhere}
      ORDER BY trigram_score DESC
      LIMIT $3 OFFSET $4
    ),
    descendants AS (
      SELECT * FROM base
      UNION ALL
      SELECT c.id, c.code, c.title, c.description, c.year, c.path, c.depth,
             0::double precision AS title_similarity,
             0::double precision AS description_similarity,
             0::double precision AS code_similarity,
             0::double precision AS trigram_score,
             NULL::text AS highlight_title,
             NULL::text AS highlight_description,
             NULL::text AS highlight_code
      FROM "NaicsCode" c
      JOIN descendants d
        ON c.year = d.year
       AND c.path LIKE d.path || '/%'
       AND c.depth > d.depth
       ${depthChildrenClause}
    )
    SELECT id, code, title, description, year, path, depth, trigram_score, title_similarity, description_similarity, code_similarity, highlight_title, highlight_description, highlight_code FROM descendants;
  `,
    query,
    year,
    limit,
    offset
  );
  const countRows = await prisma.$queryRawUnsafe<{ total: number }[]>(
    `
    SELECT COUNT(*)::int AS total
    FROM "NaicsCode"
    WHERE year = $2
      ${depthClause}
      AND ${trigramWhere};
  `,
    query,
    year
  );
  return { results: rows, total: countRows?.[0]?.total ?? rows.length };
}

/**
 * Hybrid search that unions FTS and Trigram matches, then ranks with a composite score.
 *
 * Scoring:
 *  - fts_score = ts_rank(search, websearch_to_tsquery('english', query))
 *  - trigram_score = GREATEST(similarity(title, query), similarity(coalesce(description,''), query), similarity(code, query))
 *  - composite score = fts_score * 1.0 + trigram_score * 0.7
 *
 * Deduplication:
 *  - Rows from both sets are unioned; the highest composite score per id is kept.
 *
 * Options behave the same as in FTS. When `includeChildren` is true, descendants of the top
 * matches are included with zero scores.
 */
export async function searchNaicsHybrid(
  query: string,
  options: SearchNaicsOptions = {
    year: 2022,
    limit: 10,
    offset: 0,
    includeChildren: false
  }
): Promise<NaicsCodeResult[]> {
  const { year, limit, offset, depth, includeChildren } = options;

  const depthClause = depthCondition(depth);
  const depthChildrenClause = depthCondition(depth, 'c.depth');

  const codeOnly = /^[0-9]+$/.test(query.trim());
  const ftsScoreHybrid = codeOnly
    ? `ts_rank(to_tsvector('english', code), websearch_to_tsquery('english', $1)) * 1.0`
    : `(
               ts_rank(to_tsvector('english', code), websearch_to_tsquery('english', $1)) * 1.0 +
               ts_rank(to_tsvector('english', title), websearch_to_tsquery('english', $1)) * 0.8 +
               ts_rank(to_tsvector('english', coalesce(description, '')), websearch_to_tsquery('english', $1)) * 0.6 +
               ts_rank(to_tsvector('english', coalesce(array_to_string(keywords, ' '), '')), websearch_to_tsquery('english', $1)) * 0.7
             )`;
  const ftsWhereHybrid = codeOnly
    ? `to_tsvector('english', code) @@ websearch_to_tsquery('english', $1)`
    : `(
          to_tsvector('english', code) @@ websearch_to_tsquery('english', $1)
          OR to_tsvector('english', title) @@ websearch_to_tsquery('english', $1)
          OR to_tsvector('english', coalesce(description, '')) @@ websearch_to_tsquery('english', $1)
          OR to_tsvector('english', coalesce(array_to_string(keywords, ' '), '')) @@ websearch_to_tsquery('english', $1)
        )`;
  const trigramScoreHybrid = codeOnly
    ? `similarity(code, $1) * 1.0`
    : `(
               similarity(code, $1) * 1.0 +
               similarity(title, $1) * 0.8 +
               similarity(coalesce(description, ''), $1) * 0.6 +
               similarity(coalesce(array_to_string(keywords, ' '), ''), $1) * 0.6
             )`;
  const trigramWhereHybrid = codeOnly
    ? `code % $1`
    : `(
          title % $1
          OR description % $1
          OR code % $1
          OR array_to_string(keywords, ' ') % $1
        )`;

  if (!includeChildren) {
    return prisma.$queryRawUnsafe<NaicsCodeResult[]>(
      `
      WITH fts AS (
        SELECT id, code, title, description, keywords, "crossRefs", year, path, depth,
               ${ftsScoreHybrid}::double precision AS fts_score,
               0::double precision AS trigram_score
        FROM "NaicsCode"
        WHERE year = $2
          ${depthClause}
          AND ${ftsWhereHybrid}
      ),
      trigram AS (
        SELECT id, code, title, description, keywords, "crossRefs", year, path, depth,
               0::double precision AS fts_score,
               ${trigramScoreHybrid} AS trigram_score
        FROM "NaicsCode"
        WHERE year = $2
          ${depthClause}
          AND ${trigramWhereHybrid}
      ),
      combined AS (
        SELECT *, (fts_score * 1.0 + trigram_score * 0.7)::double precision AS score
        FROM (
          SELECT * FROM fts
          UNION ALL
          SELECT * FROM trigram
        ) sub
      )
      SELECT DISTINCT ON (id) *
      FROM combined
      ORDER BY id, score DESC
      LIMIT $3 OFFSET $4;
    `,
      query,
      year,
      limit,
      offset
    );
  }

  return prisma.$queryRawUnsafe<NaicsCodeResult[]>(
    `
    WITH fts AS (
      SELECT id, code, title, description, keywords, "crossRefs", year, path, depth,
             ${ftsScoreHybrid}::double precision AS fts_score,
             0::double precision AS trigram_score
      FROM "NaicsCode"
      WHERE year = $2
        ${depthClause}
        AND ${ftsWhereHybrid}
    ),
    trigram AS (
      SELECT id, code, title, description, keywords, "crossRefs", year, path, depth,
             0::double precision AS fts_score,
             ${trigramScoreHybrid}::double precision AS trigram_score
      FROM "NaicsCode"
      WHERE year = $2
        ${depthClause}
        AND ${trigramWhereHybrid}
    ),
    combined AS (
      SELECT *, (fts_score * 1.0 + trigram_score * 0.7) AS score
      FROM (
        SELECT * FROM fts
        UNION ALL
        SELECT * FROM trigram
      ) sub
    ),
    top_matches AS (
      SELECT DISTINCT ON (id) *
      FROM combined
      ORDER BY id, score DESC
      LIMIT $3 OFFSET $4
    ),
    RECURSIVE descendants AS (
      SELECT * FROM top_matches
      UNION ALL
      SELECT c.id, c.code, c.title, c.description, c.keywords, c."crossRefs", c.year, c.path, c.depth,
             0::double precision AS fts_score,
             0::double precision AS trigram_score,
             0::double precision AS score
      FROM "NaicsCode" c
      JOIN descendants d
        ON c.year = d.year
       AND c.path LIKE d.path || '/%'
       AND c.depth > d.depth
       ${depthChildrenClause}
    )
    SELECT id, code, title, description, keywords, "crossRefs", year, path, depth, fts_score, trigram_score, score FROM descendants;
  `,
    query,
    year,
    limit,
    offset
  );
}

export interface NaicsDetails extends NaicsCodeResult {
  parent: NaicsCodeResult | null;
  ancestors: NaicsCodeResult[];
  siblings: NaicsCodeResult[];
  children: NaicsCodeResult[];
  descendants: NaicsCodeResult[];
}

export async function getNaicsDetails(
  code: string,
  year = 2022
): Promise<NaicsDetails | null> {
  const [record] = await prisma.$queryRaw<NaicsCodeResult[]>`
    SELECT id, code, title, description, keywords, "crossRefs", year, path, depth
    FROM "NaicsCode"
    WHERE code = ${code} AND year = ${year}
    LIMIT 1;
  `;
  if (!record) return null;

  const [parent, children, ancestors, siblings, descendants] =
    await Promise.all([
      getParent(code, year),
      getChildren(code, year),
      getAncestors(code, year),
      getSiblings(code, year),
      getDescendants(code, year)
    ]);

  return {
    ...record,
    parent,
    ancestors,
    siblings,
    children,
    descendants
  };
}

export async function searchNaics(
  query: string,
  options: SearchNaicsOptions = {
    year: 2022
  }
): Promise<{ results: NaicsCodeResult[]; total: number }> {
  // Priority: try Full‑Text Search first; if no results, fall back to Trigram similarity.
  // This favors precise, linguistically-relevant matches before fuzzy similarity.
  const fts = await searchNaicsFTS(query, options);
  if (fts.results.length > 0) return fts;

  return searchNaicsTrigram(query, options);
}

/* -------------------------------------------------------------------------- */
/*                          Keyword-Only Search                               */
/* -------------------------------------------------------------------------- */

/**
 * Search only the keywords (index items) for NAICS codes.
 *
 * Instead of returning full code records with scores, this unnests the
 * `keywords` array and performs a case-insensitive substring match against
 * individual keyword strings.  The result is a flat list of matching keywords
 * paired with their NAICS code and title — ideal for UIs where the user picks
 * a keyword first, then reveals the code.
 *
 * Options:
 *  - year: NAICS vintage (default 2022)
 *  - depth: restrict to specific hierarchy depth(s)
 *  - limit / offset: pagination
 *  - excludeExcept: when true (default), keywords where the search term is
 *    immediately preceded by the word "except" are filtered out.  For example,
 *    searching for "academic" would exclude "except academic".
 */
export async function searchNaicsKeywords(
  query: string,
  options: SearchKeywordsOptions = {}
): Promise<{ results: KeywordSearchResult[]; total: number }> {
  const year = options.year ?? 2022;
  const limit = options.limit ?? 25;
  const offset = options.offset ?? 0;
  const excludeExcept = options.excludeExcept ?? true;
  const depth = options.depth;

  const depthClause = depthCondition(depth);

  // Case-insensitive match: keyword ILIKE '%<term>%'
  // Except filter: keyword !~* 'except\s+<term>' (word "except" followed by whitespace + term)
  const exceptClause = excludeExcept
    ? `AND kw !~* ('except\\s+' || $1)`
    : '';

  const [rows, countRows] = await Promise.all([
    prisma.$queryRawUnsafe<KeywordSearchResult[]>(
      `
      SELECT kw AS keyword, n.code, n.title, n.year, n.depth, n.path
      FROM "NaicsCode" n,
           unnest(n.keywords) AS kw
      WHERE n.year = $2
        ${depthClause}
        AND kw ILIKE '%' || $1 || '%'
        ${exceptClause}
      ORDER BY
        -- Exact prefix matches first, then alphabetical
        CASE WHEN kw ILIKE $1 || '%' THEN 0 ELSE 1 END,
        kw,
        n.code
      LIMIT $3 OFFSET $4;
    `,
      query,
      year,
      limit,
      offset
    ),
    prisma.$queryRawUnsafe<{ total: number }[]>(
      `
      SELECT COUNT(*)::int AS total
      FROM "NaicsCode" n,
           unnest(n.keywords) AS kw
      WHERE n.year = $2
        ${depthClause}
        AND kw ILIKE '%' || $1 || '%'
        ${exceptClause};
    `,
      query,
      year
    )
  ]);

  return { results: rows, total: countRows?.[0]?.total ?? rows.length };
}
